import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FiSave, FiSun, FiSunset } from 'react-icons/fi';
import { db, functions } from '../firebase';
import { getCameraCapabilities } from '../utils/adminSettings';
import {
  SCENIC_CAPTION_LIMIT,
  SCENIC_EVENTS,
  formatScenicStatus,
  hydrateScenicSettings,
  scenicPresetOptions,
  scenicRunVideoUrl,
} from '../utils/scenicCapture';
import './ScenicCaptureSettings.css';

const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const runDate = (run, timezone) => {
  const value = run.scheduledAt?.toDate?.() || new Date(run.scheduledAt || run.localDate);
  if (Number.isNaN(value.getTime())) return run.localDate || 'Date unavailable';
  try {
    return value.toLocaleString('en-GB', { timeZone: timezone, dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) {
    return value.toISOString();
  }
};

export default function ScenicCaptureSettings({ client, cameras = [] }) {
  const clientId = client?.id || '';
  const clientName = client?.name || clientId;
  const [saved, setSaved] = useState(() => hydrateScenicSettings(client || {}));
  const savedRef = useRef(saved);
  const [draft, setDraft] = useState(saved);
  const [hasSavedSettings, setHasSavedSettings] = useState(Boolean(client?.scenicCapture));
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsError, setPresetsError] = useState('');
  const [runs, setRuns] = useState([]);
  const [runsError, setRunsError] = useState('');
  const [viewing, setViewing] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState({});
  const availableCameras = useMemo(() => cameras.filter((camera) => (
    (camera.clientId || camera.client_id) === clientId
  )), [cameras, clientId]);
  const presetOptions = useMemo(() => scenicPresetOptions(presets), [presets]);
  const scenicCamera = availableCameras.find((camera) => camera.id === draft.cameraId);
  const scenicSupported = getCameraCapabilities(scenicCamera || {}).scenicCapture;
  const unsupportedRecordingEnabled = !scenicSupported
    && (draft.sunrise.enabled || draft.sunset.enabled);
  const dirty = !same(saved, draft);

  useEffect(() => {
    if (!clientId) return undefined;
    return onSnapshot(doc(db, 'clients', clientId), (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      const next = hydrateScenicSettings({ ...data, id: clientId });
      const previousSaved = savedRef.current;
      savedRef.current = next;
      setSaved(next);
      setHasSavedSettings(Boolean(data.scenicCapture));
      setDraft((current) => (same(current, previousSaved) ? next : current));
    }, (error) => setNotice({ error: true, text: error.message || 'Unable to load scenic settings.' }));
  }, [clientId]);

  useEffect(() => {
    setPresets([]);
    setPresetsError('');
    if (!draft.cameraId) {
      setPresetsLoading(false);
      return undefined;
    }
    setPresetsLoading(true);
    return onSnapshot(collection(db, 'cameras', draft.cameraId, 'presets'), (snapshot) => {
      setPresets(snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id })));
      setPresetsLoading(false);
    }, (error) => {
      setPresetsLoading(false);
      setPresetsError(error.message || 'Unable to load camera presets.');
    });
  }, [draft.cameraId]);

  useEffect(() => {
    if (!clientId) return undefined;
    return onSnapshot(query(
      collection(db, 'scenicRuns'),
      where('clientId', '==', clientId),
      orderBy('scheduledAt', 'desc'),
      limit(10),
    ), (snapshot) => {
      setRuns(snapshot.docs.map((entry) => ({ ...entry.data(), id: entry.id })));
      setRunsError('');
    }, (error) => setRunsError(error.message || 'Unable to load recent recordings.'));
  }, [clientId]);

  const updateEvent = (event, updates) => {
    setDraft((current) => ({ ...current, [event]: { ...current[event], ...updates } }));
  };

  const viewSavedVideo = async (run) => {
    // Reserve the tab during the user gesture so the async authorization call
    // does not trigger a popup blocker. Blocked tabs get an explicit link.
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    setViewing((previous) => ({ ...previous, [run.id]: { loading: true } }));
    try {
      const response = await httpsCallable(functions, 'createScenicViewingLink')({ runId: run.id });
      const url = new URL(response?.data?.url);
      if (url.protocol !== 'https:' || url.username || url.password
        || !(Date.parse(response?.data?.expiresAt) > Date.now())) {
        throw new Error('Unable to prepare this recording. Please try again.');
      }
      if (tab && !tab.closed) tab.location.replace(url.href);
      setViewing((previous) => ({
        ...previous,
        [run.id]: tab && !tab.closed ? {} : { url: url.href, expiresAt: response.data.expiresAt },
      }));
    } catch (error) {
      if (tab && !tab.closed) tab.close();
      setViewing((previous) => ({ ...previous, [run.id]: {
        error: error.message || 'Unable to prepare this recording. Please try again.',
      } }));
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (unsupportedRecordingEnabled) {
      setNotice({ error: true, text: 'Turn off sunrise and sunset recording or choose a Dahua camera.' });
      return;
    }
    setSaving(true);
    setNotice({});
    try {
      const response = await httpsCallable(functions, 'saveClientScenicSettings')({ clientId, scenicCapture: draft });
      const next = response.data.scenicCapture;
      savedRef.current = next;
      setSaved(next);
      setDraft(next);
      setHasSavedSettings(true);
      setNotice({ text: 'Saved. Changes apply automatically to the camera.' });
    } catch (error) {
      setNotice({ error: true, text: error.message || 'Unable to save scenic settings.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settingsCard scenicSettings" aria-labelledby="scenic-settings-title">
      <header className="settingsCard__header">
        <div>
          <h2 id="scenic-settings-title">Sunrise and sunset videos</h2>
          <p>Record a silent, 30-second view and send your greeting to this property's guest WhatsApp groups.</p>
        </div>
      </header>
      <div className="settingsCard__body">
        {!clientId ? <p>Pick a location first.</p> : (
          <form onSubmit={save}>
            <fieldset disabled={saving} className="scenicSettings__fields">
              <label className="scenicSettings__field">
                <span>Scenic camera</span>
                <select
                  className="settingsSelect"
                  value={draft.cameraId}
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    cameraId: event.target.value,
                    sunrise: { ...current.sunrise, enabled: false, targetType: 'preset', presetId: null, patternId: null },
                    sunset: { ...current.sunset, enabled: false, targetType: 'preset', presetId: null, patternId: null },
                  }))}
                >
                  <option value="">Choose a camera</option>
                  {draft.cameraId && !availableCameras.some((camera) => camera.id === draft.cameraId) ? (
                    <option value={draft.cameraId}>{draft.cameraId} (unavailable)</option>
                  ) : null}
                  {availableCameras.map((camera) => (
                    <option key={camera.id} value={camera.id}>
                      {camera.displayName || camera.display_name || camera.id}{camera.enabled === false ? ' (disabled)' : ''}
                      {!getCameraCapabilities(camera).scenicCapture ? ' (scenic recording unavailable)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              {!scenicSupported ? (
                <p className="settingsInlineMeta">
                  Sunrise and sunset recording is not available for this camera type.
                  Turn off both events to save, or choose a Dahua camera.
                </p>
              ) : null}
              {!hasSavedSettings ? (
                <p className="settingsInlineMeta">
                  These are draft defaults. Recording stays off until you save and enable an event after checking its camera view.
                </p>
              ) : null}
              {presetsError ? <p role="alert" className="scenicSettings__error">{presetsError}</p> : null}
              <div className="scenicSettings__events">
                {SCENIC_EVENTS.map((event) => {
                  const settings = draft[event];
                  const label = event === 'sunrise' ? 'Sunrise' : 'Sunset';
                  const Icon = event === 'sunrise' ? FiSun : FiSunset;
                  const hasPreset = presetOptions.some((preset) => preset.value === settings.presetId);
                  const patternAvailable = clientId === 'kruger-gate-hotel' && draft.cameraId === 'kruger-gate-hotel' && event === 'sunrise';
                  return (
                    <fieldset key={event} className="scenicSettings__event">
                      <legend><Icon aria-hidden="true" /> {label}</legend>
                      <label className="scenicSettings__toggle">
                        <input
                          type="checkbox"
                          checked={settings.enabled}
                          disabled={!scenicSupported && !settings.enabled}
                          onChange={(change) => updateEvent(event, { enabled: change.target.checked })}
                        />
                        <span>Enable {event} video</span>
                      </label>
                      {patternAvailable ? (
                        <label className="scenicSettings__field">
                          <span>Sunrise camera movement</span>
                          <select className="settingsSelect" value={settings.targetType}
                            onChange={(change) => updateEvent(event, change.target.value === 'pattern' ?
                              { targetType: 'pattern', presetId: null, patternId: 2 } :
                              { targetType: 'preset', presetId: null, patternId: null })}>
                            <option value="preset">Fixed preset</option>
                            <option value="pattern">Sunrise pattern 2 (moving video)</option>
                          </select>
                        </label>
                      ) : null}
                      {settings.targetType === 'pattern' ? (
                        <p className="settingsInlineMeta">The camera records while Sunrise pattern 2 moves, then returns to its usual tour.</p>
                      ) : (
                        <label className="scenicSettings__field">
                          <span>{label} preset</span>
                          <select
                            className="settingsSelect"
                            value={settings.presetId ?? ''}
                            disabled={presetsLoading || !draft.cameraId}
                            required={settings.enabled}
                            onChange={(change) => updateEvent(event, { presetId: change.target.value ? Number(change.target.value) : null })}
                          >
                            <option value="">{presetsLoading ? 'Loading presets…' : 'Choose a preset'}</option>
                            {settings.presetId && !hasPreset ? (
                              <option value={settings.presetId}>Camera preset {settings.presetId} (needs validation)</option>
                            ) : null}
                            {presetOptions.map((preset) => (
                              <option key={preset.id} value={preset.value} disabled={preset.disabled}>{preset.label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      <label className="scenicSettings__field">
                        <span id={`scenic-${event}-caption-label`}>{label} greeting</span>
                        <textarea
                          className="settingsTextarea"
                          aria-labelledby={`scenic-${event}-caption-label`}
                          aria-describedby={`scenic-${event}-caption-count`}
                          rows={4}
                          value={settings.caption}
                          maxLength={SCENIC_CAPTION_LIMIT}
                          required={settings.enabled}
                          onChange={(change) => updateEvent(event, { caption: change.target.value })}
                        />
                        <small id={`scenic-${event}-caption-count`}>{settings.caption.length}/{SCENIC_CAPTION_LIMIT} characters</small>
                      </label>
                      <label className="scenicSettings__field">
                        <span id={`scenic-${event}-timing-label`}>{label} timing adjustment (minutes)</span>
                        <input
                          className="settingsInput"
                          aria-labelledby={`scenic-${event}-timing-label`}
                          aria-describedby={`scenic-${event}-timing-help`}
                          type="number"
                          min={-60}
                          max={60}
                          step="any"
                          value={settings.offsetSeconds / 60}
                          onChange={(change) => updateEvent(event, { offsetSeconds: Math.round(Number(change.target.value) * 60) })}
                        />
                        <small id={`scenic-${event}-timing-help`}>Negative is earlier, positive is later. Zero centres the video on {event}.</small>
                      </label>
                    </fieldset>
                  );
                })}
              </div>
              <p className="settingsInlineMeta">
                Animal tracking takes priority. A busy or manually controlled camera may skip a recording.
                Disabling an event cancels unfinished recording and pending delivery.
              </p>
              <div className="scenicSettings__actions">
                <button type="submit" className="settingsButton" disabled={saving || unsupportedRecordingEnabled}>
                  <FiSave aria-hidden="true" /> {saving ? 'Saving…' : 'Save sunrise and sunset settings'}
                </button>
                {dirty ? (
                  <button type="button" className="settingsButton settingsButton--ghost" onClick={() => setDraft(saved)}>Discard changes</button>
                ) : null}
              </div>
            </fieldset>
          </form>
        )}
        {notice.text ? <p role={notice.error ? 'alert' : 'status'} className={notice.error ? 'scenicSettings__error' : ''}>{notice.text}</p> : null}
        {clientId ? (
          <section className="scenicSettings__recent" aria-label={`Recent scenic recordings for ${clientName}`}>
            <h3>Recent recordings</h3>
            {runsError ? <p role="alert" className="scenicSettings__error">{runsError}</p> : null}
            {!runsError && !runs.length ? <p className="settingsInlineMeta">No recordings yet.</p> : null}
            <ul>
              {runs.map((run) => {
                const videoUrl = scenicRunVideoUrl(run);
                const statuses = [
                  ['Camera', run.status], ['Upload', run.uploadStatus],
                  ['Processing', run.processingStatus], ['WhatsApp', run.deliveryStatus],
                ].filter(([, status]) => Boolean(status));
                return (
                  <li key={run.id}>
                    <div className="scenicSettings__runTitle">
                      <strong>{run.eventType || run.event || 'Scenic video'}</strong>
                      <span>{runDate(run, client?.timezone || 'Africa/Johannesburg')}</span>
                    </div>
                    <p>{statuses.map(([label, status]) => `${label}: ${formatScenicStatus(status)}`).join(' · ') || 'Scheduled'}</p>
                    {run.reason ? <p>{String(run.reason).replace(/[-_]/g, ' ')}</p> : null}
                    {run.storageAssets?.media ? (
                      <>
                        <button type="button" className="settingsButton settingsButton--ghost"
                          disabled={viewing[run.id]?.loading} onClick={() => viewSavedVideo(run)}>
                          {viewing[run.id]?.loading ? 'Preparing video…' : 'View saved video'}
                        </button>
                        {viewing[run.id]?.url && Date.parse(viewing[run.id].expiresAt) > Date.now() ? (
                          <a href={viewing[run.id].url} target="_blank" rel="noopener noreferrer">Open saved video</a>
                        ) : null}
                        {viewing[run.id]?.error ? <p role="alert" className="scenicSettings__error">{viewing[run.id].error}</p> : null}
                      </>
                    ) : videoUrl ? <a href={videoUrl} target="_blank" rel="noopener noreferrer">View saved video</a> : null}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>
    </section>
  );
}
